import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { hapticLight } from "../../utils/haptics"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const closeRef = React.useRef(null)
  const sheetRef = React.useRef(null)
  const drag = React.useRef({ startY: 0, delta: 0, active: false })

  const setRefs = React.useCallback((node) => {
    sheetRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])

  const onGrabStart = (e) => {
    drag.current = { startY: e.touches[0].clientY, delta: 0, active: true }
    if (sheetRef.current) sheetRef.current.style.transition = "none"
  }
  const onGrabMove = (e) => {
    if (!drag.current.active) return
    const delta = Math.max(0, e.touches[0].clientY - drag.current.startY)
    drag.current.delta = delta
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${delta}px)`
  }
  const onGrabEnd = () => {
    if (!drag.current.active) return
    const { delta } = drag.current
    drag.current.active = false
    const sheet = sheetRef.current
    if (!sheet) return
    sheet.style.transition = "transform 0.2s ease-out"
    if (delta > 90) {
      sheet.style.transform = "translateY(110%)"
      hapticLight()
      setTimeout(() => closeRef.current?.click(), 160)
    } else {
      sheet.style.transform = ""
    }
  }

  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      className={cn(
        // ── Shared ────────────────────────────────────────────────────────────
        "fixed z-50 bg-background border border-gray-200 shadow-lg",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        // ── Mobile: bottom sheet ───────────────────────────────────────────
        "max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:top-auto",
        "max-sm:w-full max-sm:max-w-full max-sm:translate-x-0 max-sm:translate-y-0",
        "max-sm:h-[95vh] max-sm:max-h-[95vh]",
        "max-sm:rounded-t-2xl max-sm:rounded-b-none",
        "max-sm:data-[state=open]:slide-in-from-bottom",
        "max-sm:data-[state=closed]:slide-out-to-bottom",
        "max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]",
        // ── Desktop: centered dialog ───────────────────────────────────────
        "sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]",
        "sm:max-w-lg sm:max-h-[90vh] sm:rounded-3xl sm:w-full",
        "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",
        "sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]",
        "sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%]",
        // ── Timing ────────────────────────────────────────────────────────
        "duration-300 p-6 gap-4 grid",
        className
      )}
      style={{ WebkitOverflowScrolling: 'touch' }}
      {...props}>
      {/* Mobile-only grabber: drag down to dismiss */}
      <div
        className="sm:hidden absolute top-0 inset-x-0 h-7 z-40 flex items-start justify-center pt-2 touch-none"
        onTouchStart={onGrabStart}
        onTouchMove={onGrabMove}
        onTouchEnd={onGrabEnd}
        data-testid="dialog-sheet-grabber"
      >
        <span className="w-10 h-1.5 rounded-full bg-gray-300" />
      </div>
      {children}
      <DialogPrimitive.Close
        ref={closeRef}
        className="absolute right-2 top-2 sm:right-4 sm:top-4 z-50 p-2 max-sm:p-2.5 rounded-full bg-white shadow-lg opacity-100 transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none flex items-center justify-center">
        <X className="h-5 w-5 text-gray-700" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
